import { tokens } from "lib/colors"
import { FC, useContext, useRef, useState } from "react"
import Text from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import NotFoundTitle from "lib/components/NotFoundTitle"
import { toSentenceCase } from "lib/helpers"
import ColoredText from "@components/shared-components/ColoredText"
import InHouseProgram from "./InHouseProgram"
import PublicProgram from "./PublicProgram"
import EducationalOutingMeeting from "./EducationalOutingMeeting"
import TMS from "./TMS"
import CoachingMentoring from "./CoachingMentoring"
import LearningFestivalSpeaker from "./LearningFestivalSpeaker"

interface ContentWraperProps {
  activeTab: string
}

function ContentWraper({ activeTab }: ContentWraperProps) {
  const { useTranslation: t } = useContext(LanguageContext)

  switch (activeTab) {
    case "inhouse_program":
      return <InHouseProgram />
    case "public_program":
      return <PublicProgram />
    case "educational_outing_meeting":
      return <EducationalOutingMeeting />
    case "tms":
      return <TMS />
    case "coaching_mentoring":
      return <CoachingMentoring />
    case "learning_festival_speakers":
      return <LearningFestivalSpeaker />
    default:
      return <NotFoundTitle title={t("unable_to_access")} />
  }
}

const ExclusiveExperience: FC = () => {
  const { useTranslation: t, language } = useContext(LanguageContext)
  const [activeTab, setActiveTab] = useState<string>("inhouse_program")
  const scrollRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef<boolean>(false)
  const startX = useRef<number>(0)
  const scrollLeft = useRef<number>(0)

  const tabs = [
    { value: "inhouse_program", label: t("in_house_program") },
    { value: "public_program", label: toSentenceCase(t("public_program")) },
    { value: "educational_outing_meeting", label: t("educational_outing_meeting") },
    { value: "tms", label: "TMS" },
    { value: "coaching_mentoring", label: t("coaching_mentoring") },
    { value: "learning_festival_speakers", label: t("learning_festival_speakers") },
  ]

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    isDragging.current = true
    startX.current = e.clientX
    scrollLeft.current = scrollRef.current?.scrollLeft || 0
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isDragging.current || !scrollRef.current) return
    e.preventDefault()
    const x = e.clientX
    const walk = x - startX.current
    scrollRef.current.scrollLeft = scrollLeft.current - walk
  }

  const handleMouseUp = () => {
    isDragging.current = false
  }

  return (
    <section className="bg-white w-full py-8 lg:py-[72px]">
      <div className="container px-4 lg:px-0 mx-auto flex flex-col gap-4 lg:gap-8">
        <div className="w-full flex items-center justify-center">
          <Text
            weight="semibold"
            customClass="text-center text-xl lg:text-2xl font-semibold !text-neutral-80">
            <ColoredText
              text={t("exclusive_experience_training_with_PasarTrainer")}
              language={language}
              keywords={{
                EN: ["Exclusive experience"],
                ID: ["Pengalaman training eksklusif"],
              }}
              color={tokens.primary.DEFAULT}
            />
          </Text>
        </div>
        <div
          ref={scrollRef}
          role="button"
          tabIndex={0}
          className="bg-white flex items-center overflow-x-auto gap-2 lg:gap-x-6 w-full relative no-scrollbar scroll-snap-x"
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}>
          {tabs.map(({ value, label }) => (
            <div
              key={value}
              role="button"
              tabIndex={0}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onClick={() => {
                if (!isDragging.current) {
                  setActiveTab(value)
                }
              }}
              className={`flex-shrink-0 scroll-snap-start min-w-max py-[6.5px] px-3 lg:py-3 lg:px-6 rounded-full ${
                activeTab === value ? "bg-primary" : "bg-neutral-20"
              }`}>
              <Text
                weight="semibold"
                color={activeTab === value ? "light" : "muted"}
                customClass="text-sm md:text-lg">
                {label}
              </Text>
            </div>
          ))}
        </div>
        <ContentWraper activeTab={activeTab} />
      </div>
    </section>
  )
}

export default ExclusiveExperience
